// src/utils/uploadAndEmail.ts
import { uploadFileToSharePoint } from "./sharepointupload";
import { sendEmail, generateGroupedUploadEmailBody } from "./emailHelper";
import { getCurrentUserEmail } from "./userEmail";
import type { IndexRow } from "../vdr/providers";

/* ============================================================================
   SEND-ONLY: Build & send a summary email for uploaded files.
   - Includes only successfully uploaded files with valid URLs.
   - Does NOT include skipped files.
   - Suppresses email entirely if nothing was uploaded.
   ============================================================================ */

export async function sendUploadSummaryEmail(params: {
  grouped: Record<string, { name: string; url?: string; status?: string }[]>;
  folderLink: string;
  title?: string;
  indexRows?: IndexRow[];
}) {
  const { grouped, folderLink, title = "FilePigeon – Upload Summary", indexRows } = params;

  // Filter out skipped or invalid entries
  const filtered: Record<string, { name: string; url: string }[]> = {};

  for (const [folder, items] of Object.entries(grouped || {})) {
    const valid = (items || []).filter((i): i is { name: string; url: string } => {
      const status = (i as any)?.status;
      const url = (i?.url && typeof i.url === "string" ? i.url.trim() : "");
      const looksLikeFile = /\/[^\/]+\.[^\/]+$/.test(url);

      if (status === "skipped") return false;
      if (status === "done") return url.length > 0;
      // no explicit status: only accept items that look like file links
      return looksLikeFile;
    }).map(i => ({ name: i.name, url: i.url! }));

    if (valid.length) filtered[folder] = valid;
  }

  // Skip email if no valid uploads remain
  const total = Object.values(filtered).reduce((n, arr) => n + arr.length, 0);
  if (total === 0) {
    console.log("[EMAIL] No successfully uploaded files to summarize; skipping email.");
    return;
  }

  const me = await getCurrentUserEmail();
  if (!me) {
    console.warn("[EMAIL] No logged-in user email; skipping summary email.");
    return;
  }

  const html = generateGroupedUploadEmailBody(filtered, {
    title,
    rootFolderPath: folderLink,
    indexRows,
  });

  await sendEmail({
    to: [me],
    subject: "New Documents Uploaded",
    html,
  });

  console.log(`📧 Summary email sent to ${me} for ${total} uploaded file(s).`);
}

/* ============================================================================
   UPLOAD + OPTIONAL EMAIL ORCHESTRATOR
   - Uploads all files to SharePoint.
   - Tracks uploads vs. skips.
   - Sends an email only when at least one upload succeeded.
   ============================================================================ */

export async function uploadFilesAndMaybeEmail({
  files,
  folderLink,
  category,
  requestNumber,
  sendEmailAfter = false,
  title = "FilePigeon – Upload Summary",
  indexRows,
}: {
  files: File[];
  folderLink: string;
  category?: string;
  requestNumber?: string;
  sendEmailAfter?: boolean;
  title?: string;
  indexRows?: IndexRow[];
}) {
  if (!files?.length) {
    alert("No files selected.");
    return;
  }

  const runId = Math.random().toString(36).slice(2, 8).toUpperCase();
  const log = (...args: any[]) => console.log(`[UPLD ${runId}]`, ...args);
  const warn = (...args: any[]) => console.warn(`[UPLD ${runId}]`, ...args);
  const error = (...args: any[]) => console.error(`[UPLD ${runId}]`, ...args);

  const processed = new Set<string>();
  const keyOf = (f: File) => `${f.name}__${f.size}`;

  const grouped: Record<string, { name: string; url: string }[]> = {};
  const seenByFolder = new Map<string, Set<string>>();

  let uploadedCount = 0;
  let skippedCount = 0;

  for (const file of files) {
    const k = keyOf(file);
    if (processed.has(k)) {
      warn(`Duplicate in same run detected, ignoring: ${file.name}`);
      continue;
    }
    processed.add(k);

    try {
      const result = await uploadFileToSharePoint(file, folderLink);

      if (result?.skipped) {
        skippedCount++;
        const reason = result?.reason || "duplicate";
        log(`SKIPPED: ${file.name} — ${reason}`);
        continue; // skipped files never included
      }

      if (result?.success && result?.data) {
        const driveItem = result.data;
        const folderPath: string = driveItem?.parentReference?.path ?? "Unknown";
        const url: string = driveItem?.webUrl ?? "";
        const displayName: string = driveItem?.name ?? file.name;

        if (url && url.trim().length > 0) {
          if (!grouped[folderPath]) {
            grouped[folderPath] = [];
            seenByFolder.set(folderPath, new Set());
          }

          const seen = seenByFolder.get(folderPath)!;
          if (!seen.has(url)) {
            grouped[folderPath].push({ name: displayName, url });
            seen.add(url);
          }

          uploadedCount++;
          log(`UPLOADED: ${displayName}`);
        } else {
          warn(`Uploaded but no webUrl returned; excluding from email: ${displayName}`);
        }
      } else {
        warn(`Unexpected uploader result for ${file.name}:`, result);
      }
    } catch (e) {
      error(`FAILED: ${file.name}`, e);
    }
  }

  // If nothing uploaded or email not requested, stop here
  if (!sendEmailAfter || uploadedCount === 0) {
    const msg =
      skippedCount > 0
        ? `✅ Upload complete. Uploaded ${uploadedCount}, skipped ${skippedCount}.`
        : "✅ Upload complete.";
    alert(msg);
    return;
  }

  // Send summary email with only successful uploads
  try {
    const me = await getCurrentUserEmail();
    if (!me) throw new Error("Empty user email");

    const html = generateGroupedUploadEmailBody(grouped, {
      title,
      rootFolderPath: folderLink,
      indexRows,
    });

    log("Emailing upload summary to:", me);
    await sendEmail({ to: [me], subject: "New Documents Uploaded", html });

    const msg =
      skippedCount > 0
        ? `✅ Files uploaded and email sent. Uploaded ${uploadedCount}, skipped ${skippedCount}.`
        : "✅ Files uploaded and email sent.";
    alert(msg);
  } catch (e) {
    error("Email send failed:", e);
    const msg =
      skippedCount > 0
        ? `Files uploaded (${uploadedCount}) but email failed. Skipped ${skippedCount}.`
        : "Files uploaded but email failed. See console for details.";
    alert(msg);
  }
}
